import { Link } from "react-router-dom";
import { ArrowRight, Search, Sparkles, Package } from "lucide-react";
import { PRODUCTS, BRAND } from "@/data/products";

const PRINCIPLES = [
  {
    icon: Search,
    title: "Carefully Sourced",
    text: "We select our spices from trusted suppliers and established growing regions — comparing quality, aroma, appearance and consistency before anything earns The Good Spoon name.",
  },
  {
    icon: Sparkles,
    title: "Quality-Focused Processing",
    text: "Every spice is handled, processed, stored and packed with attention to cleanliness, consistency, aroma, colour and flavour.",
  },
  {
    icon: Package,
    title: "Freshness-Focused Packaging",
    text: "Food-grade barrier packaging and proper sealing protect against moisture, air, light and aroma loss, with controlled inventory cycles so nothing sits too long.",
  },
];

const Home = () => {
  return (
    <div data-testid="home-page" className="bg-cream">
      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 pattern-bg opacity-60 pointer-events-none" />
        <div className="relative px-6 sm:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="eyebrow animate-fade-in-up">Naturally Better Spices</p>
            <h1
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl md:text-7xl font-bold leading-[0.98] text-ink mt-6 animate-fade-in-up delay-100"
            >
              Naturally <span className="text-coral">better</span> spices.
            </h1>
            <p className="font-primary text-base sm:text-lg text-ink/70 mt-7 max-w-xl leading-relaxed animate-fade-in-up delay-200">
              {BRAND.name} is a modern, quality-focused spice brand — turmeric,
              coriander and chilli, each carefully sourced, cleanly processed,
              and sealed for freshness so it reaches you at its very best.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <Link to="/products" data-testid="hero-cta-products" className="btn-primary group">
                Shop the Collection
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="btn-secondary">
                Get in Touch
              </Link>
            </div>
          </div>

          <div className="relative animate-fade-in-up delay-200">
            <div className="absolute -inset-3 bg-coral/10 -z-10 hidden sm:block" />
            <img
              src={BRAND.heroImage}
              alt="Premium spices"
              className="w-full h-[52vh] lg:h-[64vh] object-cover rounded-2xl"
            />
          </div>
        </div>
      </section>

      {/* Ethos — dark section for rhythm */}
      <section className="relative bg-ink text-cream overflow-hidden">
        <div className="absolute inset-0 pattern-bg opacity-60 pointer-events-none" />
        <div className="relative px-6 sm:px-12 lg:px-24 py-24 sm:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <p className="eyebrow">What We Stand For</p>
              <h2 className="font-primary font-bold text-4xl sm:text-5xl text-cream mt-6 leading-tight">
                Naturally better, by design.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-center">
              <p className="font-primary text-lg text-cream/70 leading-relaxed">
                {BRAND.name} is built on three simple principles — the way a spice
                is sourced, the way it is processed, and the way it is packed. Get
                these right, and quality takes care of itself. That is the whole
                promise behind every spoon we offer.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-20">
            {PRINCIPLES.map((f) => (
              <div
                key={f.title}
                data-testid={`ethos-card-${f.title.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-cream/[0.04] border border-cream/10 p-8 hover:border-coral/50 transition-colors duration-300"
              >
                <f.icon size={26} className="text-coral" strokeWidth={1.6} />
                <h3 className="font-primary font-bold text-xl text-cream mt-6">{f.title}</h3>
                <p className="font-primary text-cream/60 mt-3 leading-relaxed text-sm">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product preview - alternating */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 sm:py-32">
        <p className="eyebrow">The Collection</p>
        <h2 className="font-primary font-bold text-4xl sm:text-5xl text-ink mt-5 mb-20">
          What we make
        </h2>

        <div className="flex flex-col gap-24 sm:gap-32">
          {PRODUCTS.map((p, i) => (
            <div
              key={p.id}
              data-testid={`home-product-${p.id}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-center ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:col-span-7 relative overflow-hidden group [direction:ltr]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-[58vh] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute top-5 left-5 bg-coral px-4 py-2">
                  <span className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-cream">
                    {p.label}
                  </span>
                </div>
              </div>
              <div className="md:col-span-4 md:col-start-9 [direction:ltr]">
                <p className="font-primary text-xs uppercase tracking-[0.2em] text-ink/40 italic">
                  {p.botanical}
                </p>
                <h3 className="font-primary font-bold text-3xl sm:text-4xl text-ink mt-3">
                  {p.name}
                </h3>
                <p className="font-primary text-lg text-ink/60 mt-5 leading-relaxed">
                  {p.tagline}
                </p>
                <Link
                  to="/products"
                  data-testid={`home-product-link-${p.id}`}
                  className="group inline-flex items-center gap-2 mt-8 font-primary text-sm font-semibold text-coral hover:gap-3 transition-all"
                >
                  Discover
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band — coral brand moment */}
      <section className="px-6 sm:px-12 lg:px-24 pb-24">
        <div className="relative bg-coral overflow-hidden p-12 sm:p-20 flex flex-col items-start">
          <div className="absolute inset-0 pattern-bg opacity-40 pointer-events-none" />
          <h2 className="relative font-primary font-bold text-4xl sm:text-5xl text-cream max-w-2xl leading-tight">
            Stocking a kitchen, a café, or a curiosity?
          </h2>
          <p className="relative font-primary text-lg text-cream/85 mt-6 max-w-xl leading-relaxed">
            We work with chefs, retailers and home cooks alike. Tell us what you
            need and we'll send samples.
          </p>
          <Link
            to="/contact"
            data-testid="cta-band-contact"
            className="relative group inline-flex items-center justify-center gap-3 mt-10 bg-ink text-cream font-primary font-semibold text-sm px-8 py-4 transition-colors duration-300 hover:bg-cream hover:text-ink"
          >
            Start a Conversation
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
